/**
 * Card de produto
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import { ShoppingCart, Inventory, Store } from '@mui/icons-material';
import { Produto, ProdutoPrevisaoVendas } from '@types';
import { FormatUtil, SessionUtil } from '@utils';
import { LazyCardMedia } from '@components/common';
import { ControladorQuantidade } from './ControladorQuantidade';
import { CampoPrecoEditavel } from './CampoPrecoEditavel';
import { ModalPrevisaoVendas } from './ModalPrevisaoVendas';
import { produtoService } from '@services';
import theme from '@theme';

interface ProdutoCardProps {
  produto: Produto;
  tipo?: 'visualizacao' | 'previsao' | 'pedido';
  quantidade?: number;
  precoEditado?: number;
  disponivel?: number;
  estoque?: number;
  usandoEstoqueLoja?: boolean;
  onUpdateQuantidade?: (quantidade: number) => void;
  onUpdatePreco?: (preco: number) => void;
  onAddToCart?: () => void;
  disabled?: boolean;
}

export const ProdutoCard: React.FC<ProdutoCardProps> = React.memo(({
  produto,
  tipo = 'visualizacao',
  quantidade = 0,
  precoEditado,
  disponivel,
  estoque,
  usandoEstoqueLoja = false,
  onUpdateQuantidade,
  onUpdatePreco,
  onAddToCart,
  disabled = false,
}) => {
  const [previsaoAtiva, setPrevisaoAtiva] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [previsoes, setPrevisoes] = useState<ProdutoPrevisaoVendas[]>([]);
  const [loadingPrevisoes, setLoadingPrevisoes] = useState(false);

  // Verifica se a previsão ainda está ativa
  useEffect(() => {
    if (tipo !== 'previsao') return;

    // Parse da data brasileira
    const parseDataBrasileira = (dataStr: string): Date => {
      if (!dataStr) return new Date();
      const partes = dataStr.split(' ');
      const [dia, mes, ano] = partes[0].split('/');
      const hora = partes[1] || '00:00:00';
      const [hh, mm, ss = '00'] = hora.split(':');
      return new Date(
        parseInt(ano),
        parseInt(mes) - 1,
        parseInt(dia),
        parseInt(hh),
        parseInt(mm),
        parseInt(ss)
      );
    };

    const verificarPrevisao = () => {
      const vendedor = SessionUtil.getVendedor();
      if (!vendedor || !vendedor.indPrevisaoFim) {
        setPrevisaoAtiva(false);
        return;
      }

      const agora = new Date();
      const dataFim = parseDataBrasileira(vendedor.indPrevisaoFim);
      const ativa = agora.getTime() <= dataFim.getTime();

      setPrevisaoAtiva(ativa);
    };

    // Verifica imediatamente
    verificarPrevisao();

    // Verifica a cada segundo
    const interval = setInterval(verificarPrevisao, 1000);

    return () => clearInterval(interval);
  }, [tipo]);
  const getImageUrl = (filename: string): string => {
    if (!filename) return '';
    return FormatUtil.formatImageUrl(`${filename}`);
  };

  const handleIncrement = () => {
    if (onUpdateQuantidade) {
      const newQtd = quantidade + 1;

      // Para previsão, não há limite
      if (tipo === 'previsao') {
        onUpdateQuantidade(newQtd);
        return;
      }

      // Para pedido, valida contra estoque
      if (tipo === 'pedido') {
        if (!estoque || newQtd <= estoque) {
          onUpdateQuantidade(newQtd);
        }
        return;
      }

      // Para outros tipos, valida contra disponivel
      if (!disponivel || newQtd <= disponivel) {
        onUpdateQuantidade(newQtd);
      }
    }
  };

  const handleDecrement = () => {
    if (onUpdateQuantidade) {
      const newQtd = Math.max(0, quantidade - 1);
      onUpdateQuantidade(newQtd);
    }
  };

  const handleImageClick = useCallback(async () => {
    // Só abre modal no modo pedido
    if (tipo !== 'pedido') return;

    const vendedor = SessionUtil.getVendedor();
    if (!vendedor || !vendedor.indPrevisao) {
      console.warn('Vendedor ou previsão não encontrado');
      return;
    }

    setModalOpen(true);
    setLoadingPrevisoes(true);

    try {
      const data = await produtoService.obterPrevisaoVendas(
        vendedor.indPrevisao,
        produto.codigo
      );
      setPrevisoes(data);
    } catch (error) {
      console.error('Erro ao carregar previsões:', error);
      setPrevisoes([]);
    } finally {
      setLoadingPrevisoes(false);
    }
  }, [tipo, produto.codigo]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const precoAtual = precoEditado || parseFloat(produto.precoVenda);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <LazyCardMedia
        height="160"
        image={getImageUrl(produto.imagem)}
        alt={produto.nome}
        loading="lazy"
        onClick={handleImageClick}
        sx={{
          objectFit: 'cover',
          bgcolor: 'grey.100',
          backgroundColor : theme.palette.elite.contrastText,
          cursor: tipo === 'pedido' ? 'pointer' : 'default',
          '&:hover': tipo === 'pedido' ? {
            opacity: 0.8,
            transition: 'opacity 0.2s',
          } : {},
        }}
      />

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, alignItems: 'center' }}>
        {/* Linha 2: Nome completo + Chips */}
        <Box sx={{ width: '100%', textAlign: 'center', mb: 1.5 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.3,
              minHeight: '2.6rem', // 2 linhas de altura
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 0.5
            }}
          >
            {produto.nome}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={FormatUtil.formatUnit(produto.unidade, produto.peso)}
              variant="outlined"
              color="primary"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {tipo === 'pedido' && (
              <Chip
                size="small"
                icon={<Inventory sx={{ fontSize: '0.875rem' }} />}
                label={`${produto.estoque}`}
                variant="outlined"
                color={Number(produto.estoque) > 0 ? 'success' : 'error'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {tipo === 'pedido' && Number(produto.estoque_loja) > 0 && (
              <Chip
                size="small"
                icon={<Store sx={{ fontSize: '0.875rem' }} />}
                label={`${produto.estoque_loja}`}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>

        {/* Linha 3: Preço */}
        {/* Para pedido: só exibe preço editável se estoque > 0 */}
        {tipo === 'pedido' && onUpdatePreco && estoque !== undefined && Number(estoque) > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, width: '100%' }}>
            <CampoPrecoEditavel
              valor={precoAtual}
              valorMinimo={parseFloat(produto.precoMin)}
              onChange={onUpdatePreco}
              disabled={disabled}
            />
          </Box>
        )}

        {/* Para visualização: exibe preço normal */}
        {tipo === 'visualizacao' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, width: '100%' }}>
            <Typography variant="h6" color="primary" fontWeight={600}>
              {FormatUtil.formatCurrency(precoAtual)}
            </Typography>
          </Box>
        )}

        {/* Linha 4: Controlador de Quantidade ou Botão Adicionar */}
        <Box sx={{ mt: 'auto', width: '100%' }}>
          {tipo === 'visualizacao' && onAddToCart && (
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<ShoppingCart />}
              onClick={onAddToCart}
              disabled={disabled || (disponivel !== undefined && disponivel <= 0)}
            >
              Adicionar 
            </Button>
          )}

          {(tipo === 'previsao') && onUpdateQuantidade && previsaoAtiva && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <ControladorQuantidade
                  quantidade={quantidade}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  disabled={disabled}
                  size="medium"
                  editavel
                  onChange={(valor) => onUpdateQuantidade(valor)}
                />
            </Box>
          )}

          {(tipo === 'pedido') && onUpdateQuantidade && (Number(estoque) > 0 || Number(produto.estoque_loja) > 0) && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ControladorQuantidade
                quantidade={quantidade}
                onIncrement={handleIncrement}
                onDecrement={handleDecrement}
                max={Number(estoque) > 0 ? Number(estoque) : Number(produto.estoque_loja)}
                disabled={disabled || (Number(estoque) === 0 && Number(produto.estoque_loja) === 0)}
                usandoEstoqueLoja={usandoEstoqueLoja}
                size="medium"
              />
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Modal de Previsões de Vendas */}
      <ModalPrevisaoVendas
        open={modalOpen}
        onClose={handleCloseModal}
        produtoCodigo={produto.codigo}
        produtoNome={produto.nome}
        previsoes={previsoes}
        isLoading={loadingPrevisoes}
      />
    </Card>
  );
});